import {
  showToast,
  updateToastMessage,
  updateToastProgress,
  hideToast,
  getCurrentToastToken,
} from "./components/Toasts";
import Room from "./models/Room";
import Logger from "./Logger";

import Device, { renumberDuplicatephysicalAddresses } from "./models/Device";
import { channelTypeInfo } from "./models/ChannelType";

import BaseDialog from "./modelDialogs/BaseDialog";
import { itx } from "./translationItx/itx";
import { isDevelopmentEnvironment } from "./estada-config";
import { EtsSyncConfig } from "./models/EtsSyncConfig";

const logger = new Logger();

var addressOverflow = false;
var address1Overflow = false;
var address2Overflow = false;
var address3Overflow = false;

var addressOverflowTextMsg = "";

function addressOverflowTextMsgT() {
  return state.translate(
    "export.addressOverflowText",
    "Address Level #lvl contains too many elements. Please reduce number of #items.",
  );
}

function createExportBuildingStructure(config: EtsSyncConfig | undefined) {
  var projectStructure: any = [];

  if (state.floors.items.size > 31) {
    address1Overflow = true;
    addressOverflowTextMsg = addressOverflowTextMsgT().replace("#lvl", "1").replace("#items", "rooms"); // no itx
  } else {
    state.floors.forEach((floor) => {
      // Level 1 (Floor)
      const etageExport = {
        type: "Etage",
        name: floor.name,
        etsId: floor.etsId,
        tag: floor.tag,
        id: floor.id,
        rooms: new Array(),
      };
      projectStructure.push(etageExport);

      floor.rooms.forEach((room) => {
        // Level 2 (Room)
        let tag = room.tag;
        let name = room.name;

        if (room.isFloorGeneralRoom) {
          if (room.consumers.size == 0 && room.buttons.size == 0) {
            return; // to forEach // next
          }

          tag = "-";
          name = itx("export.etageAllgemein", "Etage allgemein"); // itx
        }

        const roomExport = {
          type: "Room",
          name: name,
          etsId: room.etsId,
          tag: tag,
          id: room.id,
          functions: Array(),
        };
        etageExport.rooms.push(roomExport);

        addConsumerstoRoom(room, roomExport);

        addButtonstoRoom(room, roomExport);
        if (state.settings.knxGroupAddressGeneneration == "A" && roomExport.functions.length > 8) {
          address2Overflow = true;
          addressOverflowTextMsg = addressOverflowTextMsgT().replace("#lvl", "2").replace("#items", "devices"); // no itx
        }
      });
    });
  }

  return projectStructure;
}

function addConsumerstoRoom(room: Room, roomExport: any) {
  room.consumers.forEach((consumer) => {
    // Level 3 (Function - Consumer)
    const functionExport = {
      type: "Function",
      name: consumer.name ? consumer.name : "",
      etsId: consumer.etsId,
      tag: consumer.tag,
      id: consumer.id,
      channelType: consumer.channelType,
      prefixType: consumer.prefixType,
      addresses: Array(),
    };
    roomExport.functions.push(functionExport);

    const channel = consumer.getConnectedChannel();
    if (channel) {
      functionExport.name += `-> ${channel.parent?.fullTag}:${channel.shortName}`;
      const seen = new Set();
      consumer.signals.forEach((signal) => {
        signal.groupAddresses.forEach((groupAdress) => {
          // Level 3 GroupAddress Linking
          if (!seen.has(groupAdress.fullAddress)) {
            seen.add(groupAdress.fullAddress);
            functionExport.addresses.push({
              groupAddress: groupAdress.fullAddress,
            });
          }
        });
      });
    }
    if (functionExport.addresses.length > 256) {
      address3Overflow = true;
      addressOverflowTextMsg = addressOverflowTextMsgT().replace("#lvl", "3").replace("#items", "channels"); // no itx
    }
  });
}

function addButtonstoRoom(room: Room, roomExport: any) {
  room.buttons.forEach((button) => {
    // Level 3 (Function - Button)
    const functionExport = {
      type: "Button",
      name: button.name,
      etsId: button.etsId,
      tag: button.tag,
      physicalAddress: button.fullPhysicalAddress,
      id: button.id,
      channelType: button.channelType,
      // addresses: Array(),
    };
    roomExport.functions.push(functionExport);

    // button.channels.forEach((channel) => {
    //   channel.signals.forEach((signal) => {
    //     signal.groupAddresses.forEach((groupAdress) => {
    //       // Level 3 GroupAddress Linking
    //       functionExport.addresses.push({
    //         groupAddress: groupAdress.fullAddress,
    //       });
    //     });
    //   });
    // });
    // if (functionExport.addresses.length > 256) {
    //   address3Overflow = true;
    //   addressOverflowTextMsg = addressOverflowTextMsg.replace("#lvl", "3").replace("#items", "channels");
    // }
  });
}

function addCabinetsToBuildingStructure(projectStructure: any) {
  const cabinetsExport = {
    type: "Etage",
    name: state.cabinets.name,
    etsId: state.cabinets.etsId,
    tag: state.cabinets.tag,
    id: state.cabinets.id,
    rooms: new Array(),
  };

  projectStructure.push(cabinetsExport);

  state.cabinets.forEach((cabinet) => {
    const cabinetExport = {
      type: "Room",
      name: cabinet.nameOfDefaultName,
      etsId: cabinet.etsId,
      tag: cabinet.tag || "CC", // no itx
      id: cabinet.id,
      functions: Array(),
    };
    cabinetsExport.rooms.push(cabinetExport);

    cabinet.actors.forEach((actor) => {
      const info = channelTypeInfo(actor.channelType);
      if (!info.isSupply) {
        const actorExport = {
          type: "Device",
          name: actor.name,
          etsId: actor.etsId,
          physicalAddress: actor.fullPhysicalAddress,
          tag: actor.tag,
          id: actor.id,
          channelType: actor.channelType,
          addresses: Array(),
        };
        cabinetExport.functions.push(actorExport);

        const seen = new Set();
        actor.channels.forEach((channel) => {
          channel.signals.forEach((signal) => {
            signal.groupAddresses.forEach((groupAdress) => {
              if (!seen.has(groupAdress.fullAddress)) {
                seen.add(groupAdress.fullAddress);
                actorExport.addresses.push({
                  groupAddress: groupAdress.fullAddress,
                });
              }
            });
          });
        });
      }
    });
  });
}

function createDeviceTopology1(knxActors: any) {
  // Area / Line / Device
  let topology: any = {
    areas: [],
  };

  for (let knxActor of knxActors) {
    let mediumType: string = knxActor.instanceData.MediumType;
    const mediumTypeCollection = { TP: "1", RF: "2", PL: "3", IP: "4" };

    // MT - MediumType
    if (mediumType) {
      let areaMT = mediumType;
      let lineMT = mediumType;
      if (mediumType.includes(",")) {
        areaMT = mediumType.split(",")[1];
        lineMT = mediumType.split(",")[0];
      }
      let areaMTaddress = knxActor?.area ?? mediumTypeCollection[areaMT as keyof typeof mediumTypeCollection] ?? "1";

      const areaIndex = topology.areas.findIndex((area: any) => area.mediumtype == areaMT);
      if (areaIndex >= 0) {
        const lineIndex = topology.areas[areaIndex].lines.findIndex((line: any) => line.mediumtype == lineMT);
        if (lineIndex >= 0) {
          let foundDeviceInTopology = topology.areas[areaIndex].lines[lineIndex].devices.find(
            (obj: { id: any }) => obj.id == knxActor.id,
          );
          if (!foundDeviceInTopology) {
            topology.areas[areaIndex].lines[lineIndex].devices.push(knxActor);
          }
        } else {
          topology.areas[areaIndex].lines.push({
            address: (topology.areas[areaIndex].lines.length + 1).toString(),
            name: lineMT + " Line", // no itx
            mediumtype: lineMT,
            devices: [knxActor],
          });
        }
      } else {
        topology.areas.push({
          address: areaMTaddress,
          name: areaMT + " Area", // no itx
          mediumtype: areaMT,
          lines: [
            {
              address: knxActor?.line,
              name: lineMT + " Line", // no itx
              mediumtype: lineMT,
              devices: [knxActor],
            },
          ],
        });
      }
    }
  }
  return topology;
}

function createDeviceTopology(knxActors: any[]) {
  // Initialize topology structure
  const topology: any = { areas: [] };

  const mediumTypeCollection: Record<string, string> = {
    TP: "1",
    RF: "2",
    PL: "3",
    IP: "4",
  };

  for (const knxActor of knxActors) {
    const areaMediumType: string = "TP"; // knxActor.instanceData?.MediumType ??
    const lineMediumType: string = "TP";
    let lineMT = lineMediumType;
    let areaMT = areaMediumType;

    // let [lineMT, areaMT] = mediumType.includes(",") ? mediumType.split(",") : [mediumType, mediumType];

    const areaAddress = knxActor.area ?? mediumTypeCollection[areaMT as keyof typeof mediumTypeCollection] ?? "1";

    const lineAddress = knxActor.line ?? "1";

    // Find or create area
    let area = topology.areas.find((a: any) => a.address === areaAddress && a.mediumtype === areaMT);
    if (!area) {
      area = {
        address: areaAddress,
        name: `${areaMT} Area`,
        mediumtype: areaMT,
        lines: [],
      };
      topology.areas.push(area);
    }

    // Find or create line within the area
    let line = area.lines.find((l: any) => l.address === lineAddress && l.mediumtype === lineMT);
    if (!line) {
      line = {
        address: lineAddress,
        name: `${lineMT} Line`,
        mediumtype: lineMT,
        devices: [],
      };
      area.lines.push(line);
    }

    // Add device if not already added
    if (!line.devices.find((d: any) => d.id === knxActor.id)) {
      line.devices.push(knxActor);
    }
  }

  return topology;
}

function AskToRenumberDialog(props: { duplicatesFound: Array<Device> }) {
  const { duplicatesFound } = props;

  const lines: any = [];

  lines.push(<div>{`${duplicatesFound.length} Geräte mit doppelten physikalischen Adressen gefunden`} </div>);

  return (
    <BaseDialog
      hideXButton
      title={`${duplicatesFound.length} Geräte mit doppelten physikalischen Adressen gefunden`}
      okCaption={itx("export.renumberDevices", "Diese Geräte neu nummerieren")}
    >
      <div style={{ padding: "20px", display: "flex", flexDirection: "column" }}>{lines}</div>
    </BaseDialog>
  );
}

function checkGroupAddresses() {
  const gaAddresses_000 = state.allLevel3GroupAddresses().find((ga) => ga.fullAddress == "0/0/0");

  if (gaAddresses_000) {
    showToast({
      text: itx(
        "export.diegruppenadresse000",
        "Die Gruppenadresse 0/0/0 ist im Projekt vorhanden.\n Bitte entfernen Sie diese Adresse, bevor Sie mit der ETS-Synchronisierung fortfahren.",
      ),
      isError: true,
    });
    return false;
  }

  return true;
}

//
// this function checks for unique physical addresses in all devices
// if duplicates are found, it prompts the user to renumber them
//
async function checkUniquePhysicalAddresses(): Promise<boolean> {
  const addresses = new Set<String>();

  const duplicatesFound = new Array<Device>();

  state.allDevices().forEach((device) => {
    // devices without an application program (e.g. bus power supplies) have no physical address ("-") and can share it
    if (!channelTypeInfo(device.channelType).isSupply && device.physicalAddress != "-") {
      if (addresses.has(device.fullPhysicalAddress)) {
        duplicatesFound.push(device);
      } else {
        addresses.add(device.fullPhysicalAddress);
      }
    }
  });

  if (duplicatesFound.length > 0) {
    const res = await state.dialogManager.openDialog(<AskToRenumberDialog duplicatesFound={duplicatesFound} />);

    if (res) {
      renumberDuplicatephysicalAddresses(duplicatesFound);
      return true;
    }

    return false;
  }

  return true;
}

export function createKnxMetaData(config?: EtsSyncConfig, conversion?: boolean, etsPin?: string) {
  const addAndUpdateOnly = config && !config.syncAllDevcies;
  const paramTextImportMode = config?.paramTextImportMode || (conversion ? "complete" : "fast");
  let knxData: any = {
    topology: {
      areas: [],
    },
    groupAddresses: [],
    projectStructure: [],
    projectName: state.projectMetadata._name,
    etsGuid: conversion ? "" : state.projectMetadata.etsGuid || "",
    etsId: conversion ? "" : state.projectMetadata.etsId || "",
    etsPin: etsPin ?? state.projectMetadata.etsPin,
    conversionMode: conversion ? "true" : "false", // no itx
    syncMode: conversion ? "full" : addAndUpdateOnly ? "addAndUpdateOnly" : "full", // no itx
    paramTextImportMode, // no itx
    syncedIds: conversion ? [] : state.etsSyncedProjectIds || [],
  };

  return knxData;
}

function validateKnxData(knxData: any) {
  if (isDevelopmentEnvironment()) {
    const usageCounts = new Map<string, number>();
    knxData.topology.areas.forEach((area: any) => {
      area.lines.forEach((line: any) => {
        line.devices.forEach((device: any) => {
          device.signals.forEach((signal: any) => {
            const gaAddress = signal.address;
            usageCounts.set(gaAddress, (usageCounts.get(gaAddress) || 0) + 1);
          });
        });
      });
    });
    if (false) {
      let sumOfUsages = 0;
      usageCounts.forEach((count) => {
        sumOfUsages += count;
      });

      state.dialogManager.openDialog(
        <BaseDialog title={"Export Data Statistics"} noAbortButton={true}>
          <div>{itx("export.groupaddressusage", "Group Address Usage Count Total: ") + sumOfUsages}</div>
        </BaseDialog>,
      );
    }

    return usageCounts;
  }
}

function collectExportedGroupAddressSet(groupAddressNodes: any[]): Set<string> {
  const validAddresses = new Set<string>();

  const visit = (node: any) => {
    if (!node) return;
    if (node.level === 3 && node.fullAddress) {
      validAddresses.add(String(node.fullAddress).trim());
    }
    (node.children || []).forEach(visit);
  };

  (groupAddressNodes || []).forEach(visit);
  return validAddresses;
}

function removeStaleTopologySignalLinks(topology: any, validGroupAddresses: Set<string>) {
  if (!topology || validGroupAddresses.size === 0) return;

  (topology.areas || []).forEach((area: any) => {
    (area.lines || []).forEach((line: any) => {
      (line.devices || []).forEach((device: any) => {
        if (!Array.isArray(device.signals)) return;

        device.signals = device.signals.filter((signal: any) => {
          const address = String(signal?.address || "").trim();
          return !address || validGroupAddresses.has(address);
        });
      });
    });
  });
}

export async function createKnxData(
  includeDefaultValues: boolean,
  config?: EtsSyncConfig,
  conversion?: boolean,
  etsPin?: string,
  deviceExportToastLabel?: string,
) {
  var knxActors: any = [];
  addressOverflow = false;
  address1Overflow = false;
  address2Overflow = false;
  address3Overflow = false;

  if (!(await checkUniquePhysicalAddresses())) {
    return;
  }

  if (!checkGroupAddresses()) {
    return;
  }

  let devices = state.allDevices().filter((d) => !channelTypeInfo(d.channelType).isSupply);
  if (config && !config.syncAllDevcies && config.devicesToSync) {
    devices = config.devicesToSync;
  }

  const l = devices.filter((d) => !channelTypeInfo(d.channelType).isSupply).length;
  let i = 0;

  // Track elapsed time for the entire push-to-ETS device export phase.
  const pushExportStartTime = Date.now();
  let pushElapsedTimer: ReturnType<typeof setInterval> | null = null;
  let pushToastToken: number | undefined;
  let restoringPushToast = false;

  const getPushToastText = () => {
    const elapsed = Math.round((Date.now() - pushExportStartTime) / 1000);
    return `${deviceExportToastLabel} - ${i}/${l} (${elapsed}s)`;
  };

  const ensurePushExportToast = async (progress?: number) => {
    if (!deviceExportToastLabel) return;

    const text = getPushToastText();
    if (pushToastToken === undefined || getCurrentToastToken() !== pushToastToken) {
      if (restoringPushToast) return;
      restoringPushToast = true;
      try {
        await showToast({
          text,
          keepVisible: true,
          modal: true,
          progress: true,
        });
        pushToastToken = getCurrentToastToken();
      } finally {
        restoringPushToast = false;
      }
    } else {
      updateToastMessage(text, pushToastToken);
    }

    if (progress !== undefined) {
      updateToastProgress({ progress, toastToken: pushToastToken });
    }
  };

  // Show the toast once before the loop — updating it in-place avoids React
  // re-rendering the whole toast component on each device (which caused it to
  // visually close and reopen between devices).
  // Only show the toast when the caller explicitly provides a label (e.g. sync)
  // so that readback and export paths do not display a spurious "Push to ETS" message.
  if (l > 0 && deviceExportToastLabel) {
    await hideToast(); // clear any prior toast so this one is fully independent
    await ensurePushExportToast(0);
    // Update elapsed seconds every second so the counter ticks even when a
    // single device export takes longer than one second.
    pushElapsedTimer = setInterval(() => {
      void ensurePushExportToast();
    }, 1000);
  }
  try {
    for (const device of devices) {
      if (device.template) {
        i++;
        if (deviceExportToastLabel) {
          await ensurePushExportToast(Math.round((100 * i) / l));
        }

        const aExport = await device.exportToKnx(includeDefaultValues);
        if (aExport) {
          knxActors.push(aExport);
        }
      }
    }
  } finally {
    // Hide the progress toast immediately once the loop finishes or fails.
    // Using updateToastProgress(100) + a 1-second timer was unreliable because
    // the subsequent showToast call in syncOperation cancelled that timer, leaving
    // the "Ets Export N/N" text visible throughout the rest of data preparation.
    if (pushElapsedTimer) {
      clearInterval(pushElapsedTimer);
      pushElapsedTimer = null;
    }
    if (l > 0 && deviceExportToastLabel && pushToastToken !== undefined) {
      await hideToast(pushToastToken);
    }
  }

  // if (knxActors.length > 2) {
  //   showToast({
  //     text: state.translate(
  //       "export.tooManyDevicesInProject",
  //       "⚠️ Warning: Too many devices in project. Please review your project and ensure that it complies with the maximum device limit of 20 to avoid any issues.",
  //     ),
  //     time: 3000,
  //   });
  // }

  addressOverflow = address1Overflow || address2Overflow || address3Overflow;

  // Fixed 18.02.2025: Updated Address and Building associations to Device import should also get updated in ETS simultaneously.
  const addAndUpdateOnly = config && !config.syncAllDevcies;
  const paramTextImportMode = config?.paramTextImportMode || (conversion ? "complete" : "fast");

  let knxData: any = {
    topology: {
      areas: [],
    },
    groupAddresses: [],
    projectStructure: [],
    projectName: state.projectMetadata._name,
    etsGuid: conversion ? "" : state.projectMetadata.etsGuid || "",
    etsId: conversion ? "" : state.projectMetadata.etsId || "",
    etsPin: etsPin ?? state.projectMetadata.etsPin,
    conversionMode: conversion ? "true" : "false", // no itx
    syncMode: conversion ? "full" : addAndUpdateOnly ? "addAndUpdateOnly" : "full", // no itx
    paramTextImportMode, // no itx
    syncedIds: conversion ? [] : state.etsSyncedProjectIds || [],
  };

  //
  // creates only the need parts of the topology hierachy
  //
  knxData.topology = createDeviceTopology(knxActors);

  if (!config || config.syncGroupAddresses) {
    //
    // Groupaddresses that are exported are fixed from now on
    //
    state.addresses.forEach((a1) => {
      a1.isFixed = true;

      a1.children.forEach((a2) => {
        a2.isFixed = true;

        a2.children.forEach((a3) => {
          a3.isFixed = true;
        });
      });
    });

    const toInt = (value) => {
      const n = parseInt(value, 10);
      return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
    };

    const normalizeAndSortGroupAddressNode = (node) => {
      const fullAddress = node.fullAddress || "";
      const address =
        node.level === 3 ? fullAddress.split("/").pop() || String(node.address || "") : String(node.address || "");

      const children = (node.children || [])
        .map(normalizeAndSortGroupAddressNode)
        .sort((a, b) => toInt(a.address) - toInt(b.address));

      return {
        address,
        fullAddress,
        tag: node.tag,
        name: node.name || "",
        level: node.level,
        dpt: node.dpt == "1-1" ? "" : node.dpt || "",
        signalTypeName: node.signalTypeName || "",
        etsId: node.etsId ? String(node.etsId) : "",
        id: node.id ? String(node.id) : "",
        className: node.className || "",
        children,
      };
    };

    knxData.groupAddresses = state.addresses
      .toJSON()
      .map(normalizeAndSortGroupAddressNode)
      .sort((a, b) => toInt(a.address) - toInt(b.address));

    removeStaleTopologySignalLinks(knxData.topology, collectExportedGroupAddressSet(knxData.groupAddresses));
  }

  let projectStructure: any = [];
  if (!config || config.syncBuildingStructure) {
    projectStructure = createExportBuildingStructure(config);
    addCabinetsToBuildingStructure(projectStructure);

    const sortByTag = (items = []) => {
      return [...items].sort((a: any, b: any) => {
        const tagA = a?.tag || "";
        const tagB = b?.tag || "";
        return tagA.localeCompare(tagB, undefined, { numeric: true, sensitivity: "base" });
      });
    };

    const sortProjectStructure = (projectStructure = []) => {
      return sortByTag(projectStructure).map((floor: any) => ({
        ...floor,
        rooms: sortByTag(floor.rooms).map((room: any) => ({
          ...room,
          functions: sortByTag(room.functions).map((func: any) => ({
            ...func,
            addresses: [...(func.addresses || [])].sort((a, b) =>
              (a.groupAddress || "").localeCompare(b.groupAddress || "", undefined, {
                numeric: true,
                sensitivity: "base",
              }),
            ),
          })),
        })),
      }));
    };

    knxData.projectStructure = sortProjectStructure(projectStructure);
  }

  validateKnxData(knxData);

  return knxData;
}

function decToHexVersion(version: string): string {
  if (!version) return "0.0";
  return `${Math.floor(parseInt(version) / 16)}.${parseInt(version) % 16}`;
}

// indent json
export function formatJson(json: string): string {
  try {
    const obj = JSON.parse(json);
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    return json;
  }
}

export function downloadData(data: any, fileExtension?: string, fileName?: string, fileType?: string) {
  let formatted = data;
  // if (typeof data === "object") {
  //   formatted = JSON.stringify(data, null, 2);
  // } else if (typeof data === "string") {
  //   formatted = formatJson(data);
  // }
  const blob = new Blob([formatted], { type: fileType ?? "application/json" });
  const elem = window.document.createElement("a");
  elem.href = window.URL.createObjectURL(blob);
  elem.download = (fileName ?? state.projectMetadata._name) + (fileExtension ?? ".json"); // itx; Fixed file type so should not be translated
  document.body.appendChild(elem);
  elem.click();
  document.body.removeChild(elem);
}

export async function exportProjectAsJSON(config: EtsSyncConfig | undefined) {
  //// check if all Adresses are generated

  const knxData = await createKnxData(true, config);
  if (!knxData) {
    return;
  }

  if (addressOverflow) {
    showToast({
      text: addressOverflowTextMsgT(),
    });
    return;
  }
  const data = JSON.stringify(knxData);
  showToast({
    text: state.translate("export.projectFileDownloaded", "Project File Downloaded."),
  });

  downloadData(data, ".estada-project.json"); // no itx
}
